import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { ShieldCheck } from "lucide-react";
import { Logo } from "./Logo";
import { COMPANY } from "../data/site";

const KEY = "etprime_wholesale_ack_v1";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WholesaleGate = () => {
  const [open, setOpen] = useState(false);
  const [declined, setDeclined] = useState(false);
  const location = useLocation();
  const isLegal = location.pathname.startsWith("/legal") || location.pathname.startsWith("/admin");

  useEffect(() => {
    try {
      if (!localStorage.getItem(KEY)) setOpen(true);
    } catch {
      setOpen(true);
    }
  }, []);

  const confirm = () => {
    try {
      localStorage.setItem(KEY, new Date().toISOString());
    } catch {}
    // Record the wholesale representation server-side (timestamp, IP, UA) for audit.
    axios.post(`${API}/wholesale-confirmations`, { confirmed: true, path: location.pathname }).catch(() => {});
    setOpen(false);
  };

  // Never block legal/compliance pages — they must always be readable,
  // even before a visitor confirms wholesale status.
  if (!open || isLegal) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#05070A]/95 backdrop-blur-md" data-testid="wholesale-gate">
      <div className="relative max-w-2xl w-full bg-[#0A0D12] border border-white/10 p-8 md:p-12">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-[#C8A45A] to-transparent" />
        <Logo testId="gate-logo" />
        <div className="flex items-center gap-3 mt-8">
          <ShieldCheck className="text-[#C8A45A]" size={20} />
          <span className="overline">Wholesale clients only</span>
        </div>
        <h2 className="font-display text-2xl md:text-3xl font-light text-white mt-5 leading-tight">
          This website is intended for wholesale clients.
        </h2>
        <p className="text-[#A1A5AB] mt-5 leading-relaxed text-sm">
          {COMPANY.legalName} (ACN {COMPANY.acn}, ABN {COMPANY.abn}) is a wholesale-only business within the
          meaning of sections 761G and 761GA of the Corporations Act 2001 (Cth); it does not, and will not, provide
          financial services to retail clients. {COMPANY.afslStatus}
        </p>
        <p className="text-[#A1A5AB] mt-4 leading-relaxed text-sm">
          By selecting “I am a wholesale client” you represent that you are accessing this site in a wholesale or
          professional capacity, that you understand the reduced protections that apply to wholesale clients, and that
          you will not rely on site content as personal advice or as an offer to a retail client. This confirmation
          does not complete onboarding.
        </p>

        {declined && (
          <p className="text-[#E0A05A] text-sm mt-5 border border-[#C8A45A]/30 bg-[#C8A45A]/5 p-4" data-testid="gate-declined-msg">
            Access to this site is limited to wholesale clients. If you believe you qualify, please review our{" "}
            <Link to="/legal/wholesale-client-notice" className="underline">Wholesale Client Notice</Link>.
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-4 mt-8">
          <button
            onClick={confirm}
            data-testid="gate-confirm"
            className="flex-1 bg-[#C8A45A] text-[#0A0D12] hover:bg-[#D4B572] transition-colors px-6 py-4 text-sm font-medium tracking-wide"
          >
            I am a wholesale client — Enter
          </button>
          <button
            onClick={() => setDeclined(true)}
            data-testid="gate-decline"
            className="flex-1 border border-white/20 text-[#A1A5AB] hover:text-white hover:border-white/40 transition-colors px-6 py-4 text-sm tracking-wide"
          >
            I am not a wholesale client
          </button>
        </div>

        <p className="text-xs text-[#737882] mt-6 leading-relaxed">
          See our{" "}
          <Link to="/legal/wholesale-client-notice" className="text-[#A1A5AB] underline">Wholesale Client Notice</Link>,{" "}
          <Link to="/legal/terms" className="text-[#A1A5AB] underline">Terms of Use</Link> and{" "}
          <Link to="/legal/privacy" className="text-[#A1A5AB] underline">Privacy Policy</Link>.
        </p>
      </div>
    </div>
  );
};

export default WholesaleGate;
